import clsx from "clsx";
import { useLayoutEffect } from "react";
import { Control, FieldValues, Path, useWatch } from "react-hook-form";
import MultiSelect from "components/Form/Select/MultiSelect";
import { OptionType } from "components/Form/Select";
import { useFloatingField } from "./context";
import { sizeStyles } from "./sizeStyles";

interface FloatingMultiSelectProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  options: OptionType[];
  placeholder?: string;
  isClearable?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
}

function FloatingMultiSelect<T extends FieldValues>({
  control,
  name,
  options,
  placeholder = "",
  isClearable,
  menuPlacement,
}: FloatingMultiSelectProps<T>) {
  const { setHasValue, hasValue, size } = useFloatingField();
  const styles = sizeStyles[size];
  const value = useWatch({ control, name });

  useLayoutEffect(() => {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    setHasValue(arr.length > 0);
  }, [value, setHasValue]);

  return (
    <div className={clsx("floating-multi-select", hasValue && "chakra-react-select--has-value")}>
      <MultiSelect
        control={control}
        name={name}
        options={options}
        placeholder={placeholder}
        isClearable={isClearable}
        menuPlacement={menuPlacement}
        chakraStyles={{
          control: (provided: any) => ({
            ...provided,
            backgroundColor: "transparent",
            boxShadow: "none",
            cursor: "pointer",
          }),
          option: (provided: any) => ({
            ...provided,
            color: "fg.muted",
          }),
          valueContainer: (provided: any) => ({
            ...provided,
            paddingLeft: 0,
            paddingInlineStart: 0,
          }),
          multiValue: (provided: any) => ({
            ...provided,
            minHeight: styles.multiValueMinHeight,
          }),
          multiValueLabel: (provided: any) => ({
            ...provided,
            paddingTop: styles.multiValueLabelPaddingOverride ?? provided.paddingTop,
            paddingBottom: styles.multiValueLabelPaddingOverride ?? provided.paddingBottom,
            color: "fg.default",
          }),
          multiValueRemove: (provided: any) => ({
            ...provided,
            color: "fg.muted",
          }),
        }}
      />
    </div>
  );
}

export default FloatingMultiSelect;
